import { useEffect, useState } from 'react';
import SidePanel from '../../side-panel/side-panel';
import { SidePanelType } from '../../side-panel/types';
import ButtonPseudo from '../../button/button-pseudo';
import GeneralLedgerContentForm from './general-ledger-content-form';
import GeneralLedgerContentRead from './general-ledger-content-read';
import SidePanelLoader from '../../side-panel/loader';
import ButtonPrimary from '../../button/button-primary';
import PermissionDenied from '../../permission-denied/permission-denied';
import Popup from '../../popup/popup';
import { GeneralLedgerSidePanelTypes } from './types';
import DocumentSidePanel from '../documents/document-sidepanel';
import { CustomerType, DocumentStatus } from '../types';
import { PermissionEnum } from '../../../base-types';
import ButtonSecondary from '../../button/button-secondary';

import styles from '../transaction.module.css';

export default function GeneralLedgerSidepanel({
  panelIsOpen,
  handleClose,
  transaction,
  isLoading,
  title,
  children,
  onDelete,
  onSubmit,
  control,
  errors,
  isEditable,
  currencies,
  accountCategories,
  accountsCategoriesLoading,
  onSearchAutocomplete,
  setDebitAccounts,
  setCreditAccounts,
  setDebitAccountQuery,
  setCreditAccountQuery,
  onUpdateBalance,
  debitIsLoading,
  creditIsLoading,
  createIsLoading,
  debitAccountBalance,
  creditAccountBalance,
  debitAccounts,
  creditAccounts,
  okButtonText = 'Apply',
  debitBalanceIsLoading,
  isDirty,
  creditBalanceIsLoading,
  linkedTransactionPath,
  errorSidepanel,
  isCreateForm,
  permissionDenied,
  createAllowed,
  statusChangeError,
  internalError,
  backPath,
  debitBalances,
  creditBalances,
  allDebitAccountIsLoading,
  allCreditAccountIsLoading,
  currenciesList,
  currencyId,
  overdraftEnabled,
  debitAccountNumber,
  creditAccountNumber,
  baseCurrency,
  sidepanelExpandedKey,
  changeLogsIsLoading,
  changeLogs,
  activePermission,
  users,
  transactionChangeLogsPagination,
  changeLogsIsFetching,
  getTransactionChangeLogs,
  getTransationDocumentsAction,
  documentsIsLoading,
  documentsIsFetching,
  documents,
  documentsPagination,
  onUploadDocument,
  uploadDocumentInProgress,
  uploadDocumentIsSuccess,
  uploadDocumentErrors,
  downloadInProgress,
  onDownloadDocument,
  getTransactionDocument,
  attachedDocumentsErrors,
  uploadedDocumentsErrors,
  onDownloadSuccess,
  documentItem,
  documentIsLoading,
  imgLink,
  documentStatusChangeInProgress,
  changeCustomerDocumentsStatus,
  documentFileIsLoading,
  onMove,
  moveInProgress,
  deleteIsVisible,
  sendComment,
  commentInProgress,
  editComment,
  editCommentInProgress,
  editCommentIsSuccess,
  deleteComment,
  deleteCommentInProgress,
  deleteInProgress,
  onDeleteDocument,
  deleteIsSuccess,
  deleteDisabled,
}: GeneralLedgerSidePanelTypes): React.JSX.Element {
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [selectedDocumentName, setSelectedDocumentName] = useState('');
  const [subPanelVisible, setSubPanelVisible] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState('');
  const [transactionSidePanelVisible, setTransactionSidepanelVisible] =
    useState(panelIsOpen);
  const [activeTab, setActiveTab] = useState('GENERAL');
  const [selectedStatus, setSelectedStatus] = useState<
    DocumentStatus | undefined
  >(undefined);
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);

  useEffect(() => {
    if (onDownloadSuccess && selectedDocumentId && !downloadInProgress) {
      setSelectedDocumentId('');
    }
  }, [onDownloadSuccess, selectedDocumentId, downloadInProgress]);

  useEffect(() => {
    setTransactionSidepanelVisible(panelIsOpen);
  }, [panelIsOpen]);

  useEffect(() => {
    if (deleteIsSuccess) {
      setDeletePopupVisible(false);
      setSubPanelVisible(false);
      setTransactionSidepanelVisible(true);
      setActiveTab('DOCUMENTS');
    }
  }, [deleteIsSuccess]);

  let content;
  const enableEdit = Boolean(control);

  if (isLoading && !internalError) {
    content = <SidePanelLoader />;
  } else if (enableEdit && !internalError) {
    if (isCreateForm && !createAllowed) {
      content = <PermissionDenied />;
    } else {
      content = (
        <GeneralLedgerContentForm
          control={control}
          debitAccounts={debitAccounts}
          creditAccounts={creditAccounts}
          debitIsLoading={debitIsLoading}
          creditIsLoading={creditIsLoading}
          currencies={currencies}
          onUpdateBalance={onUpdateBalance}
          debitAccountBalance={debitAccountBalance}
          creditAccountBalance={creditAccountBalance}
          isEditable={isEditable}
          onSearchAutocomplete={onSearchAutocomplete}
          errors={errors}
          accountCategories={accountCategories}
          accountsCategoriesLoading={accountsCategoriesLoading}
          setDebitAccounts={setDebitAccounts}
          setCreditAccounts={setCreditAccounts}
          setDebitAccountQuery={setDebitAccountQuery}
          setCreditAccountQuery={setCreditAccountQuery}
          debitBalanceIsLoading={debitBalanceIsLoading}
          creditBalanceIsLoading={creditBalanceIsLoading}
          debitBalances={debitBalances}
          creditBalances={creditBalances}
          allDebitAccountIsLoading={allDebitAccountIsLoading}
          allCreditAccountIsLoading={allCreditAccountIsLoading}
          currenciesList={currenciesList}
          currencyId={currencyId}
          overdraftEnabled={overdraftEnabled}
          debitAccountNumber={debitAccountNumber}
          creditAccountNumber={creditAccountNumber}
        />
      );
    }
  } else if (permissionDenied) {
    content = <PermissionDenied />;
  } else if (internalError) {
    content = <div className={styles.internalError}>{internalError}</div>;
  } else {
    content = (
      <GeneralLedgerContentRead
        transaction={transaction}
        onDelete={onDelete}
        linkedTransactionPath={linkedTransactionPath}
        debitAccountBalance={debitAccountBalance}
        creditAccountBalance={creditAccountBalance}
        debitBalanceIsLoading={debitBalanceIsLoading}
        creditBalanceIsLoading={creditBalanceIsLoading}
        backPath={backPath}
        currenciesList={currenciesList}
        baseCurrency={baseCurrency}
        changeLogsIsLoading={changeLogsIsLoading}
        changeLogs={changeLogs}
        activePermission={activePermission}
        users={users}
        transactionChangeLogsPagination={transactionChangeLogsPagination}
        changeLogsIsFetching={changeLogsIsFetching}
        getTransactionChangeLogs={getTransactionChangeLogs}
        getTransationDocumentsAction={getTransationDocumentsAction}
        documentsIsLoading={documentsIsLoading}
        documentsIsFetching={documentsIsFetching}
        documents={documents}
        documentsPagination={documentsPagination}
        setSelectedDocumentName={setSelectedDocumentName}
        setSubPanelVisible={setSubPanelVisible}
        setSelectedDocumentId={setSelectedDocumentId}
        setTransactionSidepanelVisible={setTransactionSidepanelVisible}
        onUploadDocument={onUploadDocument}
        uploadDocumentInProgress={uploadDocumentInProgress}
        uploadDocumentIsSuccess={uploadDocumentIsSuccess}
        uploadDocumentErrors={uploadDocumentErrors}
        selectedDocumentId={selectedDocumentId}
        downloadInProgress={downloadInProgress}
        onDownloadDocument={onDownloadDocument}
        getTransactionDocument={getTransactionDocument}
        attachedDocumentsErrors={attachedDocumentsErrors}
        uploadedDocumentsErrors={uploadedDocumentsErrors}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        deleteDisabled={deleteDisabled}
      >
        {children}
      </GeneralLedgerContentRead>
    );
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={transactionSidePanelVisible}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={title}
        errorSidepanel={errorSidepanel || statusChangeError}
        sidepanelExpandedKey={sidepanelExpandedKey}
        footer={
          <>
            {isEditable && (
              <ButtonPrimary
                text={createIsLoading ? 'Loading...' : okButtonText}
                onClick={onSubmit}
                bold
                disabled={createIsLoading}
              />
            )}
            <ButtonPseudo
              text="Close"
              onClick={handleClose}
              className="button-white"
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={subPanelVisible}
        onClose={() => {
          setSubPanelVisible(false);
          setSelectedDocumentName('');
          setTransactionSidepanelVisible(true);
          setActiveTab('DOCUMENTS');
        }}
        title={selectedDocumentName}
        className={styles.subPanel}
        sidepanelExpandedKey="incoming-document-sidepanel"
        footer={
          <>
            {((transaction?.transaction?.recipientAccount?.organization
              ?.type === CustomerType.INDIVIDUAL &&
              activePermission?.includes(
                PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_UPDATE_STATUS_ACTION,
              )) ||
              (transaction?.transaction?.recipientAccount?.organization
                ?.type === CustomerType.CORPORATE &&
                activePermission?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_UPDATE_STATUS_ACTION,
                )) ||
              (transaction?.transaction?.senderAccount?.organization?.type ===
                CustomerType.INDIVIDUAL &&
                activePermission?.includes(
                  PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_UPDATE_STATUS_ACTION,
                )) ||
              (transaction?.transaction?.senderAccount?.organization?.type ===
                CustomerType.CORPORATE &&
                activePermission?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_UPDATE_STATUS_ACTION,
                ))) &&
              documentItem && (
                <>
                  {documentItem.status !== DocumentStatus.ACCEPTED &&
                    documentItem.status !== DocumentStatus.EXPIRED && (
                      <ButtonSecondary
                        text={
                          documentStatusChangeInProgress &&
                          selectedStatus === DocumentStatus.ACCEPTED
                            ? 'Loading...'
                            : 'Accept'
                        }
                        onClick={() => {
                          setSelectedStatus(DocumentStatus.ACCEPTED);

                          if (changeCustomerDocumentsStatus) {
                            changeCustomerDocumentsStatus({
                              ids: [documentItem?.id],
                              status: DocumentStatus.ACCEPTED,
                            });
                          }
                        }}
                        disabled={
                          documentStatusChangeInProgress || documentIsLoading
                        }
                        bold
                      />
                    )}
                  {documentItem.status !== DocumentStatus.REJECTED &&
                    documentItem.status !== DocumentStatus.EXPIRED && (
                      <ButtonSecondary
                        text={
                          documentStatusChangeInProgress &&
                          selectedStatus === DocumentStatus.REJECTED
                            ? 'Loading...'
                            : 'Reject'
                        }
                        className={styles.rejectStatusButton}
                        onClick={() => {
                          setSelectedStatus(DocumentStatus.REJECTED);

                          if (changeCustomerDocumentsStatus) {
                            changeCustomerDocumentsStatus({
                              ids: [documentItem?.id],
                              status: DocumentStatus.REJECTED,
                            });
                          }
                        }}
                        disabled={
                          documentStatusChangeInProgress || documentIsLoading
                        }
                        bold
                      />
                    )}
                </>
              )}
            <ButtonPseudo
              text="Close"
              onClick={() => {
                setSubPanelVisible(false);
                setSelectedDocumentName('');
                setTransactionSidepanelVisible(true);
                setActiveTab('DOCUMENTS');
              }}
              className="button-white"
              bold
            />
          </>
        }
      >
        {documentIsLoading ? (
          <SidePanelLoader />
        ) : (
          <DocumentSidePanel
            document={documentItem}
            documentFileIsLoading={documentFileIsLoading}
            downloadInProgress={downloadInProgress}
            imgLink={imgLink}
            setDeletePopupVisible={setDeletePopupVisible}
            onMove={onMove}
            moveInProgress={moveInProgress}
            onDownload={onDownloadDocument}
            deleteIsVisible={deleteIsVisible}
            customerType={
              transaction?.transaction?.recipientAccount
                ? (transaction?.transaction?.recipientAccount?.organization
                    ?.type as CustomerType)
                : (transaction?.transaction?.senderAccount?.organization
                    ?.type as CustomerType)
            }
            commentsVisible={activePermission?.includes(
              PermissionEnum.DOCUMENTS_COMMENTS_ACTION,
            )}
            sendComment={sendComment}
            editComment={editComment}
            editCommentInProgress={editCommentInProgress}
            editCommentIsSuccess={editCommentIsSuccess}
            deleteComment={deleteComment}
            deleteCommentInProgress={deleteCommentInProgress}
            commentInProgress={commentInProgress}
            activePermissions={activePermission}
            onDownloadSuccess={onDownloadSuccess}
            glTransaction
          />
        )}
      </SidePanel>
      <Popup
        title={`Delete ${documentItem?.name}`}
        onCancelClick={() => setDeletePopupVisible(false)}
        onOkClick={() =>
          onDeleteDocument && onDeleteDocument(documentItem?.id as string)
        }
        cancelButtonText="Cancel"
        okButtonText={deleteInProgress ? 'Loading...' : 'Delete'}
        isVisible={deletePopupVisible}
        isLoading={deleteInProgress}
        parent={document.getElementById('delete-popup-wrapper') as HTMLElement}
      />
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
      <div id="delete-popup-wrapper" />
    </>
  );
}
